import {connect} from 'react-redux';

import {TeamModel} from '../../../../../libs/shared/core/src/models/api/teamModel';
import {
  WorkspaceDisplayChevronActionsEnum,
  WorkspaceDisplayChevronSectionsEnum,
} from '../../../../../libs/shared/core/src/models/tracking/contexts/workspaceTracking';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {getHasCustomViews} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {InboxesPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {buildTeamWorkspaceExpandedKey} from '../../../../helpers/models/localPreferencesHelpers';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {VisualWorkspaceTeamData} from '../../../../models/api/visualWorkspaceModel';
import {AppState} from '../../../../store/appState';
import {getHasPermission} from '../../../../store/data/access/accessSelectors';
import {toggleExpandItemAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {getFindIsExpanded} from '../../../../store/data/localPreferences/localPreferencesSelectors';
import {getUserLicenseType} from '../../../../store/data/teammates/teammatesSelectors';
import {makeGetInboxesParameters} from '../../../../store/panels/inboxes/inboxesPanelSelectors';
import {isInboxesConversationsParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {findOnlySelectedId} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {TeamWorkspace} from './teamWorkspace';

/*
 * Props.
 */

export interface TeamWorkspaceContainerProps {
  panelId: InboxesPanelId;
  /** The team that this workspace is for. */
  team: TeamModel;
  /** Teams data passed from VisualWorkspaceModel */
  visualWorkspaceTeam: VisualWorkspaceTeamData;
}

/*
 * Selectors.
 */

/** Extract the specified team from the props. */
function getTeam(_state: AppState, props: TeamWorkspaceContainerProps) {
  return props.team;
}

// Find the expanded state for the specified team. */
function makeGetIsExpanded() {
  return createSelector([getTeam, getFindIsExpanded], (team, findIsExpanded) =>
    findIsExpanded(
      buildTeamWorkspaceExpandedKey(team.namespace),
      computeDefaultIsTeamWorkspaceExpanded(team),
    ),
  );
}

function makeGetHasAddInboxPermission() {
  return createSelector([getTeam, getHasPermission], (team, hasPermission) =>
    hasPermission(team.namespace, PermissionsEnum.CREATE_INBOX),
  );
}

function makeGetOnlySelectedSandboxConversationId() {
  const getInboxesParameters = makeGetInboxesParameters();
  return createSelector([getInboxesParameters, getTeam], (parameters, team) => {
    // Return only sandbox conversation IDs to avoid re-rendering P1 unnecessarily in other workspaces.
    if (!team.isSandbox || !isInboxesConversationsParameters(parameters)) {
      return undefined;
    }

    return findOnlySelectedId(parameters.selection);
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<TeamWorkspaceContainerProps>()(() => ({
  userLicenseType: getUserLicenseType,
  hasAddInboxPermission: makeGetHasAddInboxPermission(),
  isExpanded: makeGetIsExpanded(),
  onlySelectedSandboxConversationId: makeGetOnlySelectedSandboxConversationId(),
  hasCustomViews: getHasCustomViews,
}));

export const TeamWorkspaceContainer = connect(
  mapStateToProps,
  {toggleExpandItemAction},
  (stateProps, dispatchProps, ownProps) => {
    // Build the method to toggle the expanded state.
    const onToggleExpandClick = () => {
      trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_CHEVRON_CLICK, {
        location: WorkspaceDisplayChevronSectionsEnum.TEAM_SECTION,
        action: stateProps.isExpanded
          ? WorkspaceDisplayChevronActionsEnum.COLLAPSE
          : WorkspaceDisplayChevronActionsEnum.EXPAND,
      });
      dispatchProps.toggleExpandItemAction(
        buildTeamWorkspaceExpandedKey(ownProps.team.namespace),
        computeDefaultIsTeamWorkspaceExpanded(ownProps.team),
      );
    };

    // Merge the props.
    return {
      ...stateProps,
      ...ownProps,
      onToggleExpandClick,
    };
  },
)(TeamWorkspace);

/*
 * Helpers.
 */

// Expand regular teams by default, but collapse the sandbox.
function computeDefaultIsTeamWorkspaceExpanded(team: TeamModel) {
  return !team.isSandbox;
}
